import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { Kicker, Reveal } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];
const INTERVAL = 6500;

export default function SpotlightSection({ tint = true }) {
  const { items, visible, loading } = useCollection("spotlights");
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length < 2) return;
    const t = setInterval(() => {
      setDir(1);
      setIdx((i) => (i + 1) % items.length);
    }, INTERVAL);
    return () => clearInterval(t);
  }, [paused, items.length]);

  if (loading || !visible || items.length === 0) return null;
  const go = (d) => {
    setDir(d);
    setIdx((i) => (i + d + items.length) % items.length);
  };
  const s = items[idx];

  return (
    <section
      className={`relative overflow-hidden ${tint ? "bg-bone" : "bg-surface"}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="spotlight-section"
    >
      <img
        src="/brand/leaf.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute -left-16 -top-16 h-72 w-72 object-contain opacity-[0.06] md:h-96 md:w-96"
      />
      <div className="relative mx-auto max-w-[1200px] px-5 py-20 md:py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Kicker>Member spotlights</Kicker>
            <h2 className="mt-4 font-display text-3xl font-semibold leading-[1.15] text-ink md:text-[2.4rem]">
              Stories from the community.
            </h2>
          </div>
          <div className="flex items-center gap-2" data-testid="spotlight-controls">
            <button
              onClick={() => go(-1)}
              aria-label="Previous spotlight"
              data-testid="spotlight-prev"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-surface text-pine transition-colors hover:bg-pine hover:text-white"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next spotlight"
              data-testid="spotlight-next"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-surface text-pine transition-colors hover:bg-pine hover:text-white"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </Reveal>

        <div className="relative mt-12 min-h-[380px] md:min-h-[360px]">
          <AnimatePresence mode="wait" custom={dir} initial={false}>
            <motion.article
              key={s.id}
              custom={dir}
              initial={{ opacity: 0, x: dir * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -60 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="grid gap-8 rounded-[10px] border border-line bg-surface p-7 shadow-[0_10px_30px_rgba(18,33,27,0.06)] md:grid-cols-[260px_1fr] md:items-center md:gap-12 md:p-10"
              data-testid="spotlight-card"
            >
              <div className="relative mx-auto aspect-square w-48 overflow-hidden rounded-full border-4 border-bone md:w-full md:max-w-[260px]">
                {s.photo ? (
                  <img src={s.photo} alt={s.name} className="img-editorial h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-bone font-display text-5xl font-semibold text-pine/40">
                    {s.name.slice(0, 1)}
                  </div>
                )}
              </div>
              <div>
                <Quote className="h-9 w-9 text-terra" strokeWidth={1.4} />
                <p
                  className="mt-4 font-display text-2xl font-medium leading-[1.35] text-ink md:text-[2rem]"
                  data-testid="spotlight-quote"
                >
                  {s.quote}
                </p>
                <div className="mt-8 border-t border-line pt-5">
                  <p className="font-display text-lg font-semibold text-pine" data-testid="spotlight-name">
                    {s.name}
                  </p>
                  <p className="mt-0.5 text-sm text-muted">
                    {[s.role, s.institution].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </div>
            </motion.article>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex items-center gap-2" data-testid="spotlight-dots">
          {items.map((it, i) => (
            <button
              key={it.id}
              onClick={() => {
                setDir(i > idx ? 1 : -1);
                setIdx(i);
              }}
              aria-label={`Show spotlight ${i + 1}`}
              className="relative h-1.5 w-10 overflow-hidden rounded-full bg-line"
            >
              {i === idx && (
                <motion.span
                  key={`${idx}-${paused}`}
                  className="absolute inset-y-0 left-0 bg-pine"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: paused ? 0 : INTERVAL / 1000, ease: "linear" }}
                />
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
